import { useEffect, useState } from "react";
import axios from "axios";
import { Printer, X, FileDown, Share2 } from "lucide-react";
import { toast } from "sonner";
import { generatePDFFromElement, shareWhatsApp } from "../utils/pdfShare";
import { printHTML } from "../utils/printHTML";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUPPORT_PHONE = "07717637525";
const SUPPORT_PHONE_2 = "07707662447";
const COMPANY_NAME = "إدارة مكتب الأمين";
const CITY = "كربلاء المقدسة";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const formatDateTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${hh}:${mi}:${ss} ${dd}/${mm}/${yyyy}`;
  } catch {
    return iso;
  }
};

// Convert number to Arabic words (Iraqi Dinar — coarse implementation)
const tens = ["", "عشرة", "عشرون", "ثلاثون", "أربعون", "خمسون", "ستون", "سبعون", "ثمانون", "تسعون"];
const ones = ["", "واحد", "اثنان", "ثلاثة", "أربعة", "خمسة", "ستة", "سبعة", "ثمانية", "تسعة", "عشرة", "أحد عشر", "اثنا عشر", "ثلاثة عشر", "أربعة عشر", "خمسة عشر", "ستة عشر", "سبعة عشر", "ثمانية عشر", "تسعة عشر"];
const hundreds = ["", "مئة", "مئتان", "ثلاثمئة", "أربعمئة", "خمسمئة", "ستمئة", "سبعمئة", "ثمانمئة", "تسعمئة"];

const numToArabicWords = (num) => {
  num = Math.floor(Number(num) || 0);
  if (num === 0) return "صفر";
  const parts = [];
  const billions = Math.floor(num / 1_000_000_000);
  const millions = Math.floor((num % 1_000_000_000) / 1_000_000);
  const thousands = Math.floor((num % 1_000_000) / 1_000);
  const rest = num % 1_000;

  const sub999 = (n) => {
    if (n === 0) return "";
    const out = [];
    const h = Math.floor(n / 100);
    const r = n % 100;
    if (h) out.push(hundreds[h]);
    if (r < 20) {
      if (r) out.push(ones[r]);
    } else {
      const t = Math.floor(r / 10);
      const o = r % 10;
      if (o) out.push(`${ones[o]} و${tens[t]}`);
      else out.push(tens[t]);
    }
    return out.join(" و");
  };

  if (billions) parts.push(`${sub999(billions)} مليار`);
  if (millions) {
    if (millions === 1) parts.push("مليون");
    else if (millions === 2) parts.push("مليونان");
    else if (millions <= 10) parts.push(`${sub999(millions)} ملايين`);
    else parts.push(`${sub999(millions)} مليون`);
  }
  if (thousands) {
    if (thousands === 1) parts.push("ألف");
    else if (thousands === 2) parts.push("ألفان");
    else if (thousands <= 10) parts.push(`${sub999(thousands)} آلاف`);
    else parts.push(`${sub999(thousands)} ألف`);
  }
  if (rest) parts.push(sub999(rest));
  return `فقط ${parts.join(" و")} دينار لا غير`;
};

const InvoiceModal = ({ order, customerInfo, onClose, viewOnly = false }) => {
  // Product images keyed by product_id — fetched on open so old invoices
  // still get images (items don't snapshot the image at order-creation time).
  const [imgById, setImgById] = useState({});

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Fetch product images for the items in this invoice. We prefer any
  // snapshot embedded in the item itself (`image_path` / `image_url`) —
  // fall back to a per-product lookup only when missing.
  useEffect(() => {
    if (!order) return;
    const items = order.items || [];
    const missing = items
      .filter((it) => !(it.image_path || it.image_url || it.product_image))
      .map((it) => it.product_id)
      .filter(Boolean);
    if (missing.length === 0) return;
    const uniq = Array.from(new Set(missing));
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(
          uniq.map((pid) =>
            axios
              .get(`${API}/products/${pid}`)
              .then((r) => [pid, r.data?.image_path || r.data?.image_url || ""])
              .catch(() => [pid, ""])
          )
        );
        if (cancelled) return;
        const map = {};
        for (const [pid, url] of results) if (url) map[pid] = url;
        setImgById(map);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [order]);

  if (!order) return null;

  // Resolve a printable image URL for an item — checks embedded snapshot
  // first, then the fetched map. Returns "" if no image.
  // Storage paths (e.g. "jumlaty/products/xxx.jpg") are served via /api/files/{path}.
  const resolveImg = (it) => {
    const raw = it.image_path || it.image_url || it.product_image || imgById[it.product_id] || "";
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.startsWith("/")) return `${process.env.REACT_APP_BACKEND_URL}${raw}`;
    return `${API}/files/${raw}`;
  };

  const invoiceNum = order.invoice_number != null
    ? String(order.invoice_number)
    : String(order.id).slice(0, 8).toUpperCase();
  const totalQty = (order.items || []).reduce((s, it) => s + (it.quantity || 0), 0);
  // Previous debt = current ledger MINUS the unpaid portion of this very
  // invoice. customer_debt is the live total owed (already includes any
  // unpaid amount of this order), so subtracting this order's unpaid
  // portion isolates what the customer owed BEFORE this invoice.
  const orderUnpaid = Math.max(0, Number(order.amount_remaining ?? order.total ?? 0));
  const previousBalance = Math.max(0, Number(customerInfo?.customer_debt || 0) - orderUnpaid);
  const currentBalance = Number(customerInfo?.customer_debt || 0);

  const handlePrint = () => {
    // 🖨️ Fire-and-forget: mark this invoice as printed on the backend so the
    // green/red status dot on the admin board flips to green. Non-blocking —
    // never breaks the actual print flow.
    if (order?.id && !viewOnly) {
      axios
        .patch(`${API}/admin/orders/${order.id}/mark-printed`)
        .then(({ data }) => {
          // Locally reflect the change so the modal itself shows green immediately
          if (data?.printed_at) order.printed_at = data.printed_at;
        })
        .catch(() => { /* non-fatal */ });
    }
    const node = document.getElementById("invoice-print-area");
    if (!node) {
      window.print();
      return;
    }
    // Clone the invoice HTML
    const content = node.outerHTML;
    // Collect ALL current stylesheets + style tags so the printed window looks identical
    const styleLinks = Array.from(
      document.querySelectorAll('link[rel="stylesheet"], style')
    )
      .map((el) => el.outerHTML)
      .join("\n");

    // Always use the hidden-iframe helper. This is much faster than
    // window.open (no new-window overhead), never blocked by popup
    // blockers, and works the same whether opened from a modal or from
    // the invoice board. Brother/HL printers on Windows print reliably
    // this way.
    const html = `<!DOCTYPE html>
<html dir="rtl" lang="ar">
  <head>
    <meta charset="UTF-8" />
    <title>فاتورة رقم ${invoiceNum} — جُملَتي</title>
    ${styleLinks}
    <style>
      /* === Print-only sizing — force everything to fit one A4 page === */
      @page { size: A4 portrait; margin: 5mm; }
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        background: #fff !important;
        color: #000 !important;
        font-family: 'Tajawal', 'Cairo', system-ui, -apple-system, sans-serif;
        /* Lock the visible viewport to an A4 page (210mm × 297mm) so the
           preview window does NOT zoom the document by accident. */
        width: 200mm !important;
        max-width: 200mm !important;
      }
      body { padding: 0 !important; }
      #invoice-print-area {
        position: static !important;
        margin: 0 auto !important;
        padding: 8mm 6mm !important;
        box-shadow: none !important;
        /* Hard-cap the printed width so big screens do not stretch it. */
        width: 200mm !important;
        max-width: 200mm !important;
        min-width: 0 !important;
        overflow: visible !important;
        max-height: none !important;
        height: auto !important;
        font-size: 10pt !important;
        line-height: 1.35 !important;
        background: #fff !important;
        color: #000 !important;
        page-break-inside: avoid;
        page-break-after: avoid;
        break-inside: avoid;
        transform: none !important;
      }
      #invoice-print-area * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        box-sizing: border-box !important;
      }
      /* Shrink any oversized media (logo / watermark / product icons). The
         absolute selectors below cap by pixel so designer sizes don't
         create 3-page invoices on some browsers. */
      #invoice-print-area img {
        max-width: 100% !important;
        max-height: 90px !important;
        height: auto !important;
        object-fit: contain !important;
      }
      #invoice-print-area .logo, #invoice-print-area [data-role="logo"] {
        max-height: 60px !important;
        width: auto !important;
      }
      /* Item images inside the invoice table — force fixed size so they
         print consistently regardless of source resolution. */
      #invoice-print-area .invoice-item-img {
        width: 12mm !important;
        height: 12mm !important;
        max-height: 12mm !important;
        object-fit: cover !important;
        display: block !important;
        margin: 0 auto !important;
      }
      #invoice-print-area .watermark, #invoice-print-area [data-role="watermark"],
      #invoice-print-area .invoice-watermark img {
        max-width: 600px !important;
        max-height: 600px !important;
        width: 70% !important;
        opacity: 0.18 !important;
      }
      #invoice-print-area svg { max-height: 70px !important; width: auto !important; }
      #invoice-print-area table {
        font-size: 9.5pt !important;
        width: 100% !important;
        border-collapse: collapse !important;
      }
      #invoice-print-area td, #invoice-print-area th {
        padding: 3px 4px !important;
        vertical-align: middle !important;
      }
      /* No element may exceed the viewport width */
      #invoice-print-area > * { max-width: 100% !important; }
      @media print {
        html, body {
          height: auto !important;
          overflow: visible !important;
          width: auto !important;
          max-width: none !important;
        }
        #invoice-print-area {
          width: auto !important;
          max-width: 100% !important;
        }
      }
    </style>
  </head>
  <body>${content}</body>
</html>`;
    printHTML(html, { title: `فاتورة #${invoiceNum}` });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 overflow-y-auto print:bg-white print:relative print:overflow-visible"
      data-testid="invoice-modal"
    >
      {/* Toolbar (hidden on print) */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm px-4 py-3 flex justify-between items-center print:hidden">
        <h2 className="font-bold text-lg text-[#111814]">{viewOnly ? "كشف الفاتورة (عرض فقط)" : "معاينة الفاتورة"}</h2>
        <div className="flex gap-2">
          {!viewOnly && (
            <>
              <button
                onClick={handlePrint}
                data-testid="invoice-print-btn"
                className="bg-[#3B82F6] hover:bg-[#2563EB] text-white font-bold px-5 py-2 rounded-full flex items-center gap-2"
              >
                <Printer className="w-4 h-4" />
                طباعة
              </button>
          <button
            onClick={async () => {
              const node = document.getElementById("invoice-print-area");
              if (!node) { toast.error("الفاتورة غير جاهزة"); return; }
              try {
                await generatePDFFromElement(node, `فاتورة_${order?.invoice_number || ""}.pdf`);
                toast.success("تم تنزيل PDF");
              } catch (err) {
                console.error("PDF error:", err);
                toast.error("فشل إنشاء PDF");
              }
            }}
            data-testid="invoice-pdf-btn"
            className="bg-[#E85D45] hover:bg-[#c14a36] text-white font-bold px-5 py-2 rounded-full flex items-center gap-2"
          >
            <FileDown className="w-4 h-4" />
            PDF
          </button>
          <button
            onClick={() => {
              const items = (order?.items || [])
                .map((it) => `• ${it.name_ar || it.name} × ${it.quantity} = ${fmt((it.price || 0) * (it.quantity || 0))} د.ع`)
                .join("\n");
              const text =
                `*فاتورة — جُملَتي*\n` +
                `رقم الفاتورة: ${order?.invoice_number || "—"}\n` +
                `التاريخ: ${formatDateTime(order?.created_at)}\n` +
                `الزبون: ${order?.user_name || ""}${order?.market_name ? " — " + order.market_name : ""}\n` +
                (items ? `\nالمواد:\n${items}\n` : "") +
                `\nالمجموع: ${fmt(order?.total || 0)} د.ع\n` +
                `كتابةً: ${numToArabicWords(order?.total || 0)}\n` +
                `\n— ${COMPANY_NAME} —`;
              shareWhatsApp(text, order?.user_phone);
            }}
            data-testid="invoice-whatsapp-btn"
            className="bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-5 py-2 rounded-full flex items-center gap-2"
          >
            <Share2 className="w-4 h-4" />
            واتساب
          </button>
            </>
          )}
          <button
            onClick={onClose}
            data-testid="invoice-close-btn"
            className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold px-4 py-2 rounded-full flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            إغلاق
          </button>
        </div>
      </div>

      {/* Invoice */}
      <div className="max-w-4xl mx-auto bg-white text-black p-8 my-6 shadow-2xl print:shadow-none print:my-0 print:p-6 relative overflow-hidden" dir="rtl" id="invoice-print-area">
        {/* Center watermark — circular Jumlaty logo, semi-transparent so it
            sits behind all content without disturbing readability of the
            items table. Uses inline styles so it survives the print-window
            stylesheet reset. */}
        <div
          aria-hidden="true"
          className="invoice-watermark"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 0,
            overflow: "hidden",
          }}
        >
          <img
            src="/brand/jumlaty-logo-round.png"
            alt=""
            style={{
              width: "70%",
              maxWidth: "600px",
              height: "auto",
              opacity: 0.18,
              userSelect: "none",
              WebkitPrintColorAdjust: "exact",
              printColorAdjust: "exact",
            }}
          />
        </div>
        {/* Ensure all real content stays ABOVE the watermark */}
        <div className="relative" style={{ zIndex: 1 }}>
        {/* Header — Royal-wave background with Jumlaty logo + company + support phones */}
        <div
          className="rounded-lg p-4 mb-3 flex items-center justify-between gap-4 min-h-[110px] relative overflow-hidden"
          style={{
            backgroundImage: "url('/brand/print-bg-wave.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            border: "2px solid #1E3A5F",
            WebkitPrintColorAdjust: "exact",
            printColorAdjust: "exact",
          }}
        >
          {/* Slight white-tint overlay so text stays crisp on the wavy bg */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(90deg, rgba(255,255,255,0.18), rgba(255,255,255,0.05))",
              pointerEvents: "none",
            }}
          />
          {/* Circular Jumlaty logo + tagline */}
          <div className="flex items-center gap-3 mt-2 relative" style={{ zIndex: 2 }}>
            <img
              src="/brand/jumlaty-logo-round.png"
              alt="جُملتي"
              className="w-20 h-20 rounded-full shadow-lg object-cover"
              style={{
                border: "2px solid rgba(255,255,255,0.6)",
                WebkitPrintColorAdjust: "exact",
                printColorAdjust: "exact",
              }}
            />
            <div className="mt-3 flex flex-col">
              <p
                className="font-heading font-black text-3xl leading-none"
                style={{ color: "#FFFFFF", textShadow: "0 2px 6px rgba(0,0,0,0.45)" }}
              >
                جُملَتي
              </p>
              <p
                className="text-sm font-bold mt-4"
                style={{ color: "#FFFFFF", textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}
              >
                {COMPANY_NAME}
              </p>
            </div>
          </div>
          {/* Support phones */}
          <div className="text-right relative" style={{ zIndex: 2 }}>
            <p
              className="text-xs font-bold"
              style={{ color: "#FFFFFF", textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}
            >
              للدعم والاستفسار
            </p>
            <p
              className="font-bold text-lg font-mono"
              style={{ color: "#FFFFFF", textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}
            >
              {SUPPORT_PHONE}
            </p>
            <p
              className="font-bold text-lg font-mono"
              style={{ color: "#FFFFFF", textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}
            >
              {SUPPORT_PHONE_2}
            </p>
          </div>
        </div>

        {/* Invoice meta */}
        <table className="w-full border-collapse text-sm mb-1 text-black">
          <tbody>
            <tr>
              <td className="border border-black px-2 py-1 font-bold w-28 text-center">رقم القائمة</td>
              <td className="border border-black px-2 py-1 text-center font-bold">{invoiceNum}</td>
              <td className="px-2 py-1 text-center font-bold" colSpan={2}>قائمة بيع آجلة</td>
              <td className="border border-black px-2 py-1 font-bold w-24 text-center">التاريخ</td>
              <td className="border border-black px-2 py-1 text-center" dir="ltr">{formatDateTime(order.created_at)}</td>
            </tr>
            <tr>
              <td className="border border-black px-2 py-1 font-bold text-center">حضرة السيد</td>
              <td className="border border-black px-2 py-1" colSpan={5}>
                {order.user_name}
                {order.market_name && <span className="text-black"> — {order.market_name}</span>}
              </td>
            </tr>
            <tr>
              <td className="border border-black px-2 py-1 font-bold text-center">عنوان الزبون</td>
              <td className="border border-black px-2 py-1" colSpan={3}>{order.address || "—"}</td>
              <td className="border border-black px-2 py-1 font-bold text-center">رقم الزبون</td>
              <td className="border border-black px-2 py-1 text-center font-mono" dir="ltr">{order.user_phone}</td>
            </tr>
          </tbody>
        </table>

        {/* Items */}
        <table className="w-full border-collapse text-sm mb-1 text-black">
          <thead>
            <tr>
              <th className="border border-black px-2 py-1 w-10 font-bold">ت</th>
              <th className="border border-black px-1 py-1 w-14 font-bold">صورة</th>
              <th className="border border-black px-2 py-1 text-right font-bold">المادة</th>
              <th className="border border-black px-2 py-1 w-20 font-bold">العدد</th>
              <th className="border border-black px-2 py-1 w-32 font-bold">السعر</th>
              <th className="border border-black px-2 py-1 w-32 font-bold">المجموع</th>
            </tr>
          </thead>
          <tbody>
            {(order.items || []).map((it, idx) => {
              const img = resolveImg(it);
              return (
                <tr key={idx}>
                  <td className="border border-black px-2 py-1 text-center">{idx + 1}</td>
                  <td className="border border-black p-0.5 text-center align-middle">
                    {img ? (
                      <img
                        src={img}
                        alt=""
                        className="w-12 h-12 object-cover mx-auto rounded invoice-item-img"
                        style={{ printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" }}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="border border-black px-2 py-1 text-right">{it.name_ar || it.name}</td>
                  <td className="border border-black px-2 py-1 text-center" dir="ltr">{it.quantity}</td>
                  <td className="border border-black px-2 py-1 text-center" dir="ltr">{fmt(it.price)} د.ع</td>
                  <td className="border border-black px-2 py-1 text-center font-bold" dir="ltr">{fmt(it.price * it.quantity)} د.ع</td>
                </tr>
              );
            })}
            {/* Pad empty rows so the visual table stays consistent, but only if items < 4 (keep it compact for 1-page A4) */}
            {Array.from({ length: Math.max(0, 4 - (order.items?.length || 0)) }).map((_, idx) => (
              <tr key={`pad-${idx}`}>
                <td className="border border-black px-2 py-3">&nbsp;</td>
                <td className="border border-black px-2 py-3">&nbsp;</td>
                <td className="border border-black px-2 py-3">&nbsp;</td>
                <td className="border border-black px-2 py-3">&nbsp;</td>
                <td className="border border-black px-2 py-3">&nbsp;</td>
                <td className="border border-black px-2 py-3">&nbsp;</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <table className="w-full border-collapse text-sm mb-2 text-black">
          <tbody>
            <tr>
              <td className="border border-black px-2 py-1 font-bold w-28 text-center">المباع الواصل</td>
              <td className="border border-black px-2 py-1 text-center" dir="ltr">0</td>
              <td className="border border-black px-2 py-1 font-bold w-28 text-center">قيمة هذه الفاتورة</td>
              <td className="border border-black px-2 py-1 text-center font-bold" dir="ltr">{fmt(order.total)} د.ع</td>
            </tr>
            <tr>
              <td className="border border-black px-2 py-1 font-bold text-center">عدد الكراتين</td>
              <td className="border border-black px-2 py-1 text-center font-bold" dir="ltr">{totalQty}</td>
              <td className="border border-black px-2 py-1 font-bold text-center">الدين المتبقي السابق</td>
              <td className="border border-black px-2 py-1 text-center font-bold" dir="ltr">{fmt(Math.max(0, previousBalance))} د.ع</td>
            </tr>
            {/* Manual discount row — Feb 14, 2026: shown only when admin
                applied a خصم at assign-mujahez time. */}
            {Number(order.manual_discount || 0) > 0 && (
              <tr className="bg-green-100">
                <td className="border border-black px-2 py-1 font-black text-center" colSpan={2}>
                  💰 الخصم على الفاتورة
                </td>
                <td className="border border-black px-2 py-1 text-center font-black text-base text-green-800" colSpan={2} dir="ltr">
                  −{fmt(order.manual_discount)} د.ع
                </td>
              </tr>
            )}
            <tr className="bg-yellow-200">
              <td className="border border-black px-2 py-1 font-black text-center" colSpan={2}>المجموع الكلي المطلوب</td>
              <td className="border border-black px-2 py-1 text-center font-black text-lg" colSpan={2} dir="ltr">
                {fmt(Number(order.total || 0) + Math.max(0, previousBalance))} د.ع
              </td>
            </tr>
          </tbody>
        </table>

        {/* Amount in words */}
        <p className="text-center text-sm font-bold mb-2 border border-black rounded p-2 text-black">
          {numToArabicWords(order.total)}
        </p>

        {/* Notes */}
        <div className="border-t border-black pt-2 mb-3">
          <p className="text-sm font-bold mb-1 text-black">الملاحظات</p>
          <p className="text-xs text-black min-h-[20px]">{order.notes || ""}</p>
        </div>

        {/* Footer */}
        <div className="flex justify-between text-xs text-black border-t border-black pt-2 font-bold">
          <span>1 من صفحة 1</span>
          <span>
            {COMPANY_NAME} — العراق — {CITY} — {SUPPORT_PHONE} — {SUPPORT_PHONE_2}
          </span>
          <span>www.jumlaty.online</span>
        </div>
        </div>{/* /content-above-watermark */}
      </div>

      <style>{`
        @media print {
          /* Inline-print fallback (in case popup is blocked) */
          body * { visibility: hidden; }
          #invoice-print-area, #invoice-print-area * { visibility: visible; }
          #invoice-print-area {
            position: absolute;
            top: 0; left: 0;
            width: 100%;
            margin: 0 !important;
            padding: 6mm !important;
            box-shadow: none !important;
            max-height: none !important;
            overflow: visible !important;
          }
          @page { size: A4 portrait; margin: 6mm; }
        }
      `}</style>
    </div>
  );
};

export default InvoiceModal;
